import { sha256 } from 'hash.js';
import { print } from 'graphql/language/printer';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { createHttpLink, gql, ApolloClient, ApolloLink } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { RestLink } from 'apollo-link-rest';
import { relayStylePagination } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import config from '../config';
import Bugsnag from './bugsnag';
import { hostname } from './helpers';
import { getXPlatform } from './helpers/getSSRPlatform';
import { ItemConnectionSort } from '../graphql/types';

const isBrowser = typeof window !== 'undefined';
let apolloClient = null;
let currentClientLang = null;

function create(
  initialState,
  { lang, platform, countryCode, ipAddr, userAgent, signature, timestamp }
) {
  const persistedQueryLink = createPersistedQueryLink({
    useGETForHashedQueries: true,
    generateHash: query => {
      return sha256().update(print(query)).digest('hex');
    }
  });

  const restLink = new RestLink({
    uri: `${config.protocol}://${hostname(lang, config.host)}/`
  });

  const httpLink = createHttpLink({
    uri: `${config.protocol}://${hostname(lang, config.host)}/graphql`,
    credentials: 'include'
  });

  const headersMiddleware = new ApolloLink((operation, forward) => {
    const xPlatform = getXPlatform(platform);

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...(countryCode ? { 'X-Vercel-Client-IPCountry': countryCode } : {}),
        ...(timestamp ? { 'X-Vercel-Timestamp': timestamp.toString() } : {}),
        ...(signature ? { 'X-Vercel-Signature': signature } : {}),
        ...(ipAddr ? { 'X-Vercel-Client-IP': ipAddr } : {}),
        ...(userAgent ? { 'User-Agent': userAgent } : {}),
        ...(xPlatform && { 'X-Platform': xPlatform })
      }
    }));

    return forward(operation);
  });

  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors)
      Bugsnag.notify(new Error('GraphQlError'), {
        beforeSend(report) {
          const reportData = report;
          graphQLErrors.map(({ message, locations, path }) => {
            reportData.metaData = {
              'Error Message': {
                message,
                locations,
                path
              }
            };

            return reportData;
          });
        }
      });
  });

  const link = errorLink
    .concat(restLink)
    .concat(headersMiddleware)
    .concat(persistedQueryLink)
    .concat(httpLink);

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          items: {
            keyArgs: ['filter', 'sort'],
            merge: (existing = [], incoming, { args }) => {
              return !args?.offset ? incoming : [...existing, ...incoming];
            }
          },
          itemsConnection: relayStylePagination(['filter', 'sort'])
        }
      },
      PaymentMethod: {
        keyFields: ['name']
      }
    }
  }).restore(initialState || {});
  const defaultData = {
    rooms: [
      {
        id: '1',
        slug: '1-otaqli',
        name: '1 комната',
        __typename: 'Room'
      },
      {
        id: '2',
        slug: '2-otaqli',
        name: '2 комнаты',
        __typename: 'Room'
      },
      {
        id: '3',
        slug: '3-otaqli',
        name: '3 комнаты',
        __typename: 'Room'
      },
      {
        id: '4',
        slug: '4-otaqli',
        name: '4 комнаты',
        __typename: 'Room'
      },
      {
        id: '5+',
        slug: '5-otaqli',
        name: '',
        __typename: 'Room'
      }
    ],
    type: [
      {
        id: 'false',
        name: 'Alqi-satqi',
        __typename: 'Type'
      },
      {
        id: 'true',
        name: 'Kiraye',
        __typename: 'Type'
      }
    ],
    sort: [
      {
        id: '1',
        params: 'bumped_at+desc',
        name: ItemConnectionSort.BumpedAtDesc,
        __typename: 'Sort'
      },
      {
        id: '2',
        params: 'area',
        name: ItemConnectionSort.AreaAsc,
        __typename: 'Sort'
      },
      {
        id: '3',
        params: 'price+asc',
        name: ItemConnectionSort.PriceAsc,
        __typename: 'Sort'
      },
      {
        id: '4',
        params: 'price+desc',
        name: ItemConnectionSort.PriceDesc,
        __typename: 'Sort'
      },
      {
        id: '5',
        params: 'price_per_are+asc',
        name: ItemConnectionSort.PricePerAreAsc,
        __typename: 'Sort'
      },
      {
        id: '6',
        params: 'price_per_are+desc',
        name: ItemConnectionSort.PricePerAreDesc,
        __typename: 'Sort'
      }
    ]
  };

  if (!process.browser) {
    const query = gql`
      query defaultQuery {
        rooms @client {
          id
          slug
          name
        }
        type @client {
          id
          name
        }
        sort @client {
          id
          params
          name
        }
      }
    `;

    cache.writeQuery({
      query,
      data: defaultData
    });
  }

  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    connectToDevTools: isBrowser,
    ssrMode: !isBrowser, // Disables forceFetch on the server (so queries are only run once)
    link,
    cache,
    resolvers: {}
  });
}

export default function initApollo(initialState, options) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return create(initialState, {
      ...options
    });
  }

  // On client-side, recreate client if language changed
  // This ensures correct API endpoint is used after language switch
  const newLang = options?.lang;
  if (!apolloClient || (newLang && newLang !== currentClientLang)) {
    apolloClient = create(initialState, options);
    currentClientLang = newLang;
  }

  return apolloClient;
}
